import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import DOMPurify from 'isomorphic-dompurify';

/**
 * Service to render text generated HTML.
 * Avoids harmful html being rendered. (XSS scripting security vulnerabilty)
 */

@Injectable({
  providedIn: 'root',
})
export class ContentRenderingService {
  readonly ALLOWED_TAGS = ['b', 'i', 'em', 'strong', 'a', 'p', 'ul', 'li'];
  readonly ALLOWED_ATTR = ['href', 'target'];

  constructor(private sanitizer: DomSanitizer) {}

  sanitizeHtml(htmlRawData: string | null | void): SafeHtml {
    if (!htmlRawData) {
      return '';
    }

    // Sanitize the raw HTML using DOMPurify
    const sanitizedHtml = DOMPurify.sanitize(htmlRawData, {
      ALLOWED_TAGS: this.ALLOWED_TAGS,
      ALLOWED_ATTR: this.ALLOWED_ATTR,
    });

    // Mark the sanitized HTML as safe for Angular
    return this.sanitizer.bypassSecurityTrustHtml(sanitizedHtml);
  }

  addNewLines(stringData: any): string {
    // if has bullets, let addBullets handled it
    if (stringData.includes('* ')) return stringData;

    // Detects '|' char to emit as new p-tag. works as new line.
    const parts = stringData.split('|');
    const paragraphs = parts
      .map((part: any) => `<p>${part.trim()}</p>`)
      .join('');
    return paragraphs;
  }

  formatBoldText(description: string): string {
    if (!description.includes('**')) return description;

    // Replace **text** with <b>text</b> (Markdown-like formatting)
    return description.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
  }

  addHyperlinks(description: string): string {
    // Match Markdown-style links [title](url)
    const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

    // Replace with <a href="url" target="_blank">title</a>
    return description.replace(
      markdownLinkRegex,
      '<a href="$2" target="_blank">$1</a>'
    );
  }

  addBullets(description: string): string {
  if (!description) return '';

  const paragraphs = description.split('|');
  let result = '';

  paragraphs.forEach((paragraph) => {
    const trimmedParagraph = paragraph.trim();
    
    if (trimmedParagraph.includes('* ')) {
      const parts = trimmedParagraph.split('* ');
      // Get all bullet items (skip the first part if it's not a bullet)
      const bulletItems = parts.slice(1);
      
      if (bulletItems.length > 0) {
        result += '<ul>';
        bulletItems.forEach(item => {
          result += `<li>${item.trim()}</li>`;
        });
        result += '</ul>';
      }
    } else {
      // Regular paragraph without bullets
      if (trimmedParagraph) {
        result += `<p>${trimmedParagraph}</p>`;
      }
    }
  });

  return result;
  }
}
